import _ from 'underscore';
import { Scopes } from '@bingads-webui-campaign-react/goal-selector';
import { DeviceKeys, Devices } from '@bingads-webui-campaign-react/xandr-campaign';
import { getBudgetRange } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';
import { UAPPlacementTypeAlias as EPT } from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';
import { CampaignSubTypes } from '@bingads-webui-campaign-react/constants';
import { ROTATION_OPTIONS } from '@bingads-webui-react/ad-rotation';
import { generateNameSuggestionSuffix } from '@bingads-webui-campaign/audience-campaign-shared';
import { getDaysInTheFuture } from '../../shared/helper/format-date';
import { AdGroupCriterions, AgeRanges, GenderTypes } from '../../common/constants';
import { getBidRecommendations } from '../../shared/components/bid-recommendations-data';


export const AdPreviewSurfacePlacementKeys = {
  Msn: ['msan_191x100', 'edge_178x100', 'msan_150x100', 'msan_1x1', 'msan_133x100'],
};

const getDefaultBidForCPCBiddingScheme = (i18n, serverContext) => {
  const bid = _.isUndefined(serverContext.RecommendCPCBid) || _.isNull(serverContext.RecommendCPCBid) ? 3.3 : serverContext.RecommendCPCBid;
  return i18n.formatDecimal(bid, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

export const getDefaultCampaign = (i18n, serverContext) => {
  const { DynamicPermissions = {} } = serverContext || {};
  const {
    CampaignLifetimeBudget,
    MaeLifetimeBudgetExclusion,
  } = DynamicPermissions;

  const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

  let lifetimeBudgetProperties = {};
  if (isCampaignLifetimeBudgetEnabled) {
    lifetimeBudgetProperties = {
      StartDate: getDaysInTheFuture(2),
      UseCampaignLevelDates: true,
    };
  }

  return ({
    data: {
      Status: 'Paused',
      Name: generateNameSuggestionSuffix(i18n.getString(_TL_('Audience Static'))),
      Budget: {
        Amount: getBudgetRange(serverContext.CurrentAccount.CurrencyCode).Daily.Default,
        Type: 'DailyStandard',
      },
      Languages: [
        'All',
      ],
      BiddingScheme: {
        Type: 'EnhancedCpc',
      },
      CampaignSubType: 0,
      CampaignType: 'Audience',
      conversionGoals: {
        originalConversionGoals: [],
        goalSelection: [],
        goalScopeSelection: Scopes.ACCOUNT,
      },
      ...lifetimeBudgetProperties,
    },
    isValid: true,
    isDirty: false,
    messages: [],
  });
};

export const getDefaultAdGroup = (i18n, serverContext) => {
  const bidRecommendations = getBidRecommendations(serverContext, CampaignSubTypes.AudienceStatic);

  return {
    data: {
      ...AdGroupCriterions,
      Status: 'Paused',
      Name: generateNameSuggestionSuffix(i18n.getString(_TL_('Ad group'))),
      StartDate: getDaysInTheFuture(2),
      locationTargets: {
        bidMode: false,
        currentLevelSettings: {
          initLocations: [],
          peopleInLocation: true,
          peopleSearchingForLocation: true,
          targetOption: 'everywhere',
        },
        parentLevelSettings: {},
        useParentLevel: false,
      },
      adSchedules: {
        dayTimeRanges: [],
      },
      deviceTypes: [
        DeviceKeys.Computers,
        DeviceKeys.Tablets,
        DeviceKeys.Smartphones,
      ],
      initialDeviceTypes: Object.keys(Devices).map(device => ({
        criterion: device,
        bidAdjustment: -100,
      })),
      ageRanges: [
        AgeRanges.EighteenToTwentyFive,
        AgeRanges.TwentyFiveToThirtyFive,
        AgeRanges.ThirtyFiveToFifty,
        AgeRanges.FiftyToSixtyFive,
        AgeRanges.SixtyFiveAndAbove,
        AgeRanges.Unknown,
      ],
      initialAgeRanges: [],
      genderTypes: [
        GenderTypes.Female,
        GenderTypes.Male,
        GenderTypes.Unknown,
      ],
      TargetSetting: 'Age;Gender',
      initialGenderTypes: [],
      audiences: [],
      initialAudiences: [],
      interests: [],
      initialInterests: [],
      FrequencyCapSettings: [
        {},
      ],
      genreTargets: [],
      initialIndustryTargets: [],
      initialNegativeIndustryTargets: [],
      initialGenreTargets: [],
      initialJobTargets: [],
      initialNegativeJobTargets: [],
      initialCompanyNameTargets: [],
      initialNegativeCompanyNameTargets: [],
      companyNameTargets: [],
      negativeCompanyNameTargets: [],

      CpmBid: bidRecommendations.CpmBid,
      SearchBid: bidRecommendations.SearchBid || getDefaultBidForCPCBiddingScheme(i18n, serverContext),

      reachEstimation: null,
      UseOptimizedTargeting: true,
      AdRotation: ROTATION_OPTIONS.OPTIMIZED_FOR_CLICKS,
    },
    isValid: true,
    isDirty: true,
    messages: [],
  };
};


export const AdModes = {
  SINGLE_AD: 'SINGLE_AD',
  MULTI_AD: 'MULTI_AD',
};

//
// Ad Preview Constants
export const extraPlacementInfo = {
  [EPT.Responsive.MSAN.WideImage]: {
    previewTitle: _TL_('MSN feed'),
    previewHeaderTitle: _TL_('Ad preview (1.91 : 1 aspect ratio)'),
    customClassName: 'msan_191x100',
    imagePreviewHeight: 200,
    disclaimerMessage: _TL_('This is an example of how your ad could appear on other websites on the Microsoft Advertising Network. The final ad format is likely to change to match the look and feel of the website.'),
    editingPlacementType: EPT.MSAN.WideImage,
  },
  [EPT.Responsive.MSAN.MSEdgeSingleCarousel]: {
    previewTitle: _TL_('Microsoft Edge single carousel'),
    previewHeaderTitle: _TL_('Ad preview (1.78 : 1 aspect ratio)'),
    customClassName: 'edge_178x100',
    imagePreviewHeight: 220,
    editingPlacementType: EPT.MSAN.MSEdgeSingleCarousel,
  },
  [EPT.Responsive.MSAN.MSNInArticle]: {
    previewTitle: _TL_('MSN in-article ad'),
    previewHeaderTitle: _TL_('Ad preview (1.5 : 1 aspect ratio)'),
    customClassName: 'msan_150x100',
    imagePreviewHeight: 200,
    editingPlacementType: EPT.MSAN.MSNInArticle,
  },
  [EPT.Responsive.MSAN.MSNSmallHeadline]: {
    previewTitle: _TL_('MSN small headline'),
    previewHeaderTitle: _TL_('Ad preview (1.33 : 1 aspect ratio)'),
    customClassName: 'msan_133x100',
    imagePreviewHeight: 75,
    editingPlacementType: EPT.MSAN.MSNSmallHeadline,
  },
  [EPT.Responsive.MSAN.SquareImage]: {
    previewTitle: _TL_('MSN square image'),
    previewHeaderTitle: _TL_('Ad preview (1 : 1 aspect ratio)'),
    customClassName: 'msan_1x1',
    imagePreviewHeight: 75,
    disclaimerMessage: _TL_('This is an example of how your ad could appear on other websites on the Microsoft Advertising Network. The final ad format is likely to change to match the look and feel of the website.'),
    editingPlacementType: EPT.MSAN.SquareImage,
  },
  [EPT.Responsive.Outlook.Mobile]: {
    previewTitle: _TL_('Microsoft Outlook web (mobile)'),
    customClassName: 'outlook_text_mobile',
    editingPlacementType: EPT.Outlook.Mobile,
  },
  [EPT.Responsive.Outlook.Web]: {
    previewTitle: _TL_('Microsoft Outlook web (computer)'),
    customClassName: 'outlook_text_web',
    editingPlacementType: EPT.Outlook.Web,
  },
  [EPT.Responsive.Boost.WindowsSpotlight]: {
    previewTitle: _TL_('Windows Spotlight'),
    previewHeaderTitle: _TL_('Ad preview'),
    editingPlacementType: EPT.Boost.WindowsSpotlight,
    hideHeaderButtons: true,
  },
  [EPT.Responsive.Boost.DiscoverCard]: {
    previewTitle: _TL_('Discover card'),
    previewHeaderTitle: _TL_('Ad preview'),
    editingPlacementType: EPT.Boost.DiscoverCard,
    hideHeaderButtons: true,
  },
  [EPT.Responsive.Boost.StartMenu]: {
    previewTitle: _TL_('Start Menu'),
    previewHeaderTitle: _TL_('Ad preview'),
    editingPlacementType: EPT.Boost.StartMenu,
    hasLogo: true,
    hideHeaderButtons: true,
  },
  [EPT.Responsive.Boost.ActionCenter]: {
    previewTitle: _TL_('Action Center'),
    previewHeaderTitle: _TL_('Ad preview'),
    editingPlacementType: EPT.Boost.ActionCenter,
    hasLogo: true,
    hideHeaderButtons: true,
  },
  [EPT.Responsive.Boost.Banner]: {
    previewTitle: _TL_('Banner'),
    previewHeaderTitle: _TL_('Ad preview'),
    editingPlacementType: EPT.Boost.Banner,
    hasLogo: true,
    hideHeaderButtons: true,
  },
  [EPT.Responsive.Boost.Prong1Prong2Feed]: {
    previewTitle: _TL_('Prong 1 / Prong 2 / Feed'),
    previewHeaderTitle: _TL_('Ad preview'),
    editingPlacementType: EPT.Boost.Prong1Prong2Feed,
    hideHeaderButtons: true,
  },
  [EPT.Responsive.Boost.Coachmark]: {
    previewTitle: _TL_('CoachMark'),
    previewHeaderTitle: _TL_('Ad preview'),
    editingPlacementType: EPT.Boost.Coachmark,
    hideHeaderButtons: true,
  },
};

export const responsiveMsanMMAConfig = [{
  placementSetName: 'responsiveMsanMMAConfig',
  placementTypes: [
    EPT.Responsive.MSAN.WideImage,
    EPT.Responsive.MSAN.MSEdgeSingleCarousel,
    EPT.Responsive.MSAN.MSNInArticle,
    EPT.Responsive.MSAN.MSNSmallHeadline,
    EPT.Responsive.MSAN.SquareImage,
    EPT.Responsive.Outlook.Web,
    EPT.Responsive.Outlook.Mobile,
  ],
}];

export const msanMMAConfig = [{
  placementSetName: 'msanMMAConfig',
  placementTypes: [
    EPT.MSAN.WideImage,
    EPT.MSAN.MSEdgeSingleCarousel,
    EPT.MSAN.MSNInArticle,
    EPT.MSAN.MSNSmallHeadline,
    EPT.MSAN.SquareImage,
    EPT.Outlook.Web,
    EPT.Outlook.Mobile,
  ],
}];

export const responsiveMsanMMABoostConfig = [{
  placementSetName: 'responsiveMsanMMABoostConfig',
  placementTypes: [
    EPT.Responsive.MSAN.WideImage,
    EPT.Responsive.MSAN.MSEdgeSingleCarousel,
    EPT.Responsive.MSAN.MSNInArticle,
    EPT.Responsive.MSAN.MSNSmallHeadline,
    EPT.Responsive.MSAN.SquareImage,
    EPT.Responsive.Boost.WindowsSpotlight,
    EPT.Responsive.Boost.DiscoverCard,
    EPT.Responsive.Boost.StartMenu,
    EPT.Responsive.Boost.ActionCenter,
    EPT.Responsive.Boost.Banner,
    EPT.Responsive.Boost.Prong1Prong2Feed,
    EPT.Responsive.Boost.Coachmark,
  ],
}];

export const msanMMABoostConfig = [{
  placementSetName: 'msanMMABoostConfig',
  placementTypes: [
    EPT.MSAN.WideImage,
    EPT.MSAN.MSEdgeSingleCarousel,
    EPT.MSAN.MSNInArticle,
    EPT.MSAN.MSNSmallHeadline,
    EPT.MSAN.SquareImage,
    EPT.Boost.WindowsSpotlight,
    EPT.Boost.DiscoverCard,
    EPT.Boost.StartMenu,
    EPT.Boost.ActionCenter,
    EPT.Boost.Banner,
    EPT.Boost.Prong1Prong2Feed,
    EPT.Boost.Coachmark,
  ],
}];
